"use client"

import { useState, useEffect } from "react"
import { MdLocationOn, MdDirectionsCar } from "react-icons/md"
import { FaRegMoneyBillAlt } from "react-icons/fa"
import axios from "axios"
import Swal from "sweetalert2"

const TransportDetailsModal = ({ isOpen, onClose, transport, userToken }) => {
  const [loading, setLoading] = useState(false)
  const [editedData, setEditedData] = useState({})
  const [currentTransport, setCurrentTransport] = useState(transport)
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(0)

  useEffect(() => {
    if (isOpen && transport) {
      setEditedData({ ...transport })
      fetchTransportData()
    }
  }, [isOpen, transport])

  const fetchTransportData = async () => {
    if (!transport || !transport.id) return

    setLoading(true)
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_PREFIX}/loadtransport/${transport.id}`, {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      })
      setCurrentTransport(response.data.data)
    } catch (error) {
      console.error("Error fetching transport data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleChangeVehicleData = (field, value) => {
    setEditedData((prev) => {
      const updatedLoadItems = [...(prev.load_items || currentTransport.load_items)]
      updatedLoadItems[selectedVehicleIndex] = {
        ...updatedLoadItems[selectedVehicleIndex],
        [field]: value,
      }
      return { ...prev, load_items: updatedLoadItems }
    })
  }

  const handleSaveAll = async () => {
    setLoading(true)
    try {
      const payload = {
        ...editedData,
        load_items: editedData.load_items || currentTransport.load_items,
      }

      await axios.post(
        `${process.env.REACT_APP_API_PREFIX}/loadtransport-update-view-details/${currentTransport.id}`,
        payload,
        {
          headers: {
            "Screen-Name": "/Load",
            Authorization: `Bearer ${userToken}`,
          },
        },
      )

      setCurrentTransport(editedData)
      Swal.fire({
        title: "Success!",
        text: "Transport details updated successfully!",
        icon: "success",
        timer: 4000,
        timerProgressBar: true,
        showConfirmButton: false,
      })
      onClose()
    } catch (error) {
      console.error("Error updating transport:", error)
      Swal.fire({
        title: "Error",
        text: "Failed to update transport details.",
        icon: "error",
        timer: 4000,
        timerProgressBar: true,
        showConfirmButton: false,
      })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !currentTransport) return null

  return (
    <div
      className="fixed inset-0 flex justify-center items-start pt-10 z-50 bg-opacity-50"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="bg-white w-full max-w-4xl mx-4 sm:mx-6 lg:mx-8 p-4 sm:p-6 lg:p-8 rounded-lg shadow-xl overflow-auto transform transition-all duration-300 ease-out">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Transport Details</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 rounded p-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="overflow-y-auto scrollbar-custom" style={{ maxHeight: "70vh" }}>
            <div className="flex flex-wrap lg:flex-nowrap">
              <div className="p-5 w-full lg:w-1/2">
                <Section title="General Information" icon={<FaRegMoneyBillAlt />}>
                  <hr className="" />
                  <div className="flex flex-wrap -mx-2 mt-4">
                    <div className="w-1/2 sm:w-1/2 px-2">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Order ID:
                      </label>
                      <input
                        type="text"
                        value={editedData.order_id || ""}
                        onChange={(e) => setEditedData((prev) => ({ ...prev, order_id: e.target.value }))}
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div className="w-1/2 sm:w-1/2 px-2">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Payment Rate:
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={editedData.payment_rate || ""}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === "" || (!isNaN(value) && Number.parseFloat(value) >= 0)) {
                            setEditedData((prev) => ({ ...prev, payment_rate: value }))
                          }
                        }}
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>
                </Section>

                <Section title="Vehicle Details" icon={<MdDirectionsCar />}>
                  <hr className="" />
                  <div className="w-full mb-4 mt-4">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Select Vehicle:
                    </label>
                    <select
                      value={selectedVehicleIndex}
                      onChange={(e) => setSelectedVehicleIndex(Number(e.target.value))}
                      className="block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      {currentTransport.load_items &&
                        currentTransport.load_items.map((item, index) => (
                          <option key={index} value={index}>
                            Vehicle {index + 1}: {item.make} {item.model} ({item.year})
                          </option>
                        ))}
                    </select>
                  </div>

                  {currentTransport.load_items && currentTransport.load_items[selectedVehicleIndex] && (
                    <div className="flex flex-wrap -mx-2 mb-4 mt-4">
                      <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                          VIN:
                        </label>
                        <input
                          type="text"
                          value={
                            editedData.load_items?.[selectedVehicleIndex]?.vin ||
                            currentTransport.load_items[selectedVehicleIndex].vin
                          }
                          onChange={(e) => handleChangeVehicleData("vin", e.target.value)}
                          className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                          Year:
                        </label>
                        <input
                          type="text"
                          maxLength={4}
                          value={
                            editedData.load_items?.[selectedVehicleIndex]?.year ||
                            currentTransport.load_items[selectedVehicleIndex].year
                          }
                          onChange={(e) => handleChangeVehicleData("year", e.target.value)}
                          className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                          Make:
                        </label>
                        <input
                          type="text"
                          value={
                            editedData.load_items?.[selectedVehicleIndex]?.make ||
                            currentTransport.load_items[selectedVehicleIndex].make
                          }
                          onChange={(e) => handleChangeVehicleData("make", e.target.value)}
                          className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                          Model:
                        </label>
                        <input
                          type="text"
                          value={
                            editedData.load_items?.[selectedVehicleIndex]?.model ||
                            currentTransport.load_items[selectedVehicleIndex].model
                          }
                          onChange={(e) => handleChangeVehicleData("model", e.target.value)}
                          className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  )}
                </Section>
              </div>

              <div className="p-5 w-full lg:w-1/2">
                <Section title="Pickup Information" icon={<MdLocationOn />}>
                  <hr className="" />
                  <div className="flex flex-wrap -mx-2 mt-4">
                    <div className="w-full px-2 mt-4">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Pickup Address:
                      </label>
                      <input
                        type="text"
                        value={editedData.pickup_address || ""}
                        onChange={(e) => setEditedData((prev) => ({ ...prev, pickup_address: e.target.value }))}
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Pickup City:
                      </label>
                      <input
                        type="text"
                        value={editedData.pickup_city || ""}
                        onChange={(e) => setEditedData((prev) => ({ ...prev, pickup_city: e.target.value }))}
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Pickup State:
                      </label>
                      <input
                        type="text"
                        value={editedData.pickup_state || ""}
                        onChange={(e) => setEditedData((prev) => ({ ...prev, pickup_state: e.target.value }))}
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>
                </Section>

                <Section title="Delivery Information" icon={<MdLocationOn />}>
                  <hr className="" />
                  <div className="flex flex-wrap -mx-2 mt-4">
                    <div className="w-full px-2 mt-4">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Delivery Address:
                      </label>
                      <input
                        type="text"
                        value={editedData.delivery_address || currentTransport.delivery_address || ""}
                        onChange={(e) =>
                          setEditedData((prev) => ({
                            ...prev,
                            delivery_address: e.target.value,
                          }))
                        }
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Delivery City:
                      </label>
                      <input
                        type="text"
                        value={editedData.delivery_city || currentTransport.delivery_city}
                        onChange={(e) =>
                          setEditedData((prev) => ({
                            ...prev,
                            delivery_city: e.target.value,
                          }))
                        }
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div className="w-1/2 sm:w-1/2 px-2 mt-4">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Delivery State:
                      </label>
                      <input
                        type="text"
                        value={editedData.delivery_state || currentTransport.delivery_state}
                        onChange={(e) =>
                          setEditedData((prev) => ({
                            ...prev,
                            delivery_state: e.target.value,
                          }))
                        }
                        className="appearance-none block w-full bg-white text-gray-700 border border-gray-300 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>
                </Section>
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end sm:justify-center lg:justify-end mt-4">
          <button className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600" onClick={handleSaveAll}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

const Section = ({ title, children, icon }) => (
  <div className="mb-6">
    <h3 className="text-xl font-semibold flex items-center mb-4">
      {icon && <span className="mr-2 text-lg">{icon}</span>}
      {title}
    </h3>
    {children}
  </div>
)

export default TransportDetailsModal
